import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { XCircle } from "lucide-react";
import { api } from "@/lib/api";

const BLOCKED = ["work_started", "in_progress", "completed", "cancelled", "declined", "closed"];

export default function SelfCancel({ booking, onCancelled }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  if (BLOCKED.includes(booking.status)) return null;
  const err = (e) => toast.error(e.response?.data?.detail || "Something went wrong");

  const getQuote = async () => {
    setBusy(true);
    try { const { data } = await api.post("/bookings/cancel-quote", { code: booking.code, phone }); setQuote(data); }
    catch (e) { err(e); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/bookings/cancel", { code: booking.code, phone, reason, expected_fee: quote.cancellation_fee });
      toast.success("Booking cancelled"); setOpen(false); onCancelled();
    } catch (e) { err(e); if (e.response?.status === 409) getQuote(); } finally { setBusy(false); }
  };
  const close = () => { setOpen(false); setQuote(null); };

  return (
    <>
      <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => setOpen(true)} data-testid="track-cancel-btn"><XCircle size={16} className="mr-2"/>Cancel booking</Button>
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md" data-testid="track-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel booking {booking.code}</DialogTitle>
            <DialogDescription>Confirm your mobile number to see the exact cancellation charge before you decide.</DialogDescription>
          </DialogHeader>
          {!quote ? (
            <div className="space-y-3">
              <div><Label>Mobile number used for booking</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="10-digit mobile" data-testid="track-cancel-phone"/></div>
              <div className="flex justify-end"><Button className="bg-orange-500 hover:bg-orange-600" disabled={busy || phone.replace(/\D/g, "").length < 10} onClick={getQuote} data-testid="track-cancel-quote-btn">See cancellation charge</Button></div>
            </div>
          ) : !quote.cancellable ? (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="track-cancel-blocked">{quote.reason}</div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm space-y-1" data-testid="track-cancel-quote">
                <div className="flex justify-between"><span className="text-slate-500">Time before start</span><span className="font-mono">{quote.hours_before_start} h</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Booking total</span><span className="font-mono">₹{quote.amount}</span></div>
                <div className="flex justify-between font-bold"><span>Cancellation charge ({quote.fee_pct}%)</span><span className="font-mono text-red-600" data-testid="track-cancel-fee">{quote.cancellation_fee ? `₹${quote.cancellation_fee}` : "Free"}</span></div>
                {quote.amount_paid > 0 && <div className="flex justify-between"><span className="text-slate-500">Refund to you</span><span className="font-mono text-emerald-700" data-testid="track-cancel-refund">₹{quote.refundable_from_paid}</span></div>}
                {quote.amount_paid === 0 && quote.cancellation_fee > 0 && <p className="text-xs text-slate-500 pt-1">No payment has been collected yet. Our team will contact you about the charge.</p>}
              </div>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="track-cancel-reason"/>
              <div className="flex justify-between">
                <Button variant="outline" onClick={close} data-testid="track-cancel-keep">Keep booking</Button>
                <Button className="bg-red-600 hover:bg-red-700" disabled={busy} onClick={confirm} data-testid="track-cancel-confirm">Confirm cancellation</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
